import { useState, useEffect } from 'react';
import api from '../services/api';
import { usePermissions } from '../context/PermissionsContext';
import InfoTooltip from '../components/ui/InfoTooltip';

const TIPO_LABEL = {
  primario: 'Primário',
  secundario: 'Secundário',
  trabalho: 'Trabalho',
  transferencia: 'Transferência',
};

const TIPO_COLOR = {
  primario:     'bg-purple-900/60 text-purple-300 border-purple-700/60',
  secundario:   'bg-blue-900/60 text-blue-300 border-blue-700/60',
  trabalho:     'bg-emerald-900/60 text-emerald-300 border-emerald-700/60',
  transferencia: 'bg-amber-900/60 text-amber-300 border-amber-700/60',
};

const EMPTY_FORM = {
  tag: '', descricao: '', tipo: 'trabalho', origem: 'interno',
  fabricante: '', modelo: '', numeroDeSerie: '', grandeza: '', unidade: '',
  faixaMin: '', faixaMax: '',
  lastCalibrationDate: '', nextCalibrationDate: '',
  numeroCertificado: '', laboratorioId: '',
  incertezaExpandida: '', fatorK: '2',
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ date }) {
  const d = daysUntil(date);
  if (d === null) return <span className="text-slate-500 text-xs">—</span>;
  if (d < 0) return <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/60 text-red-300 border border-red-700/60">Vencido</span>;
  if (d <= 30) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-900/60 text-amber-300 border border-amber-700/60">Vence em {d}d</span>;
  return <span className="text-xs text-slate-400">{new Date(date).toLocaleDateString('pt-BR')}</span>;
}

export default function PadroesReferenciaPage() {
  const { can } = usePermissions();
  const canEdit = can('padroes', 'edit');

  const [padroes, setPadroes] = useState([]);
  const [laboratorios, setLaboratorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterOrigem, setFilterOrigem] = useState('');
  const [labPopup, setLabPopup] = useState(null);

  useEffect(() => {
    // Sincroniza instrumentos classificacao='padrao' que ainda não têm PadraoReferencia, depois carrega
    api.post('/instruments/sync-padroes').catch(() => {}).finally(() => carregar());
    api.get('/laboratorios').then(r => setLaboratorios(r.data)).catch(() => {});
  }, []);

  async function carregar() {
    setLoading(true);
    try {
      const r = await api.get('/padroes-referencia?incluirInativos=false');
      setPadroes(r.data);
    } catch {
      setErro('Erro ao carregar padrões.');
    } finally {
      setLoading(false);
    }
  }

  function abrirNovo() {
    setEditando(null);
    setForm(EMPTY_FORM);
    setErro('');
    setShowModal(true);
  }

  function abrirEditar(p) {
    setEditando(p);
    setForm({
      tag: p.tag || '',
      descricao: p.descricao || '',
      tipo: p.tipo || 'trabalho',
      origem: p.origem || 'interno',
      fabricante: p.fabricante || '',
      modelo: p.modelo || '',
      numeroDeSerie: p.numeroDeSerie || '',
      grandeza: p.grandeza || '',
      unidade: p.unidade || '',
      faixaMin: p.faixaMin != null ? String(p.faixaMin) : '',
      faixaMax: p.faixaMax != null ? String(p.faixaMax) : '',
      lastCalibrationDate: p.lastCalibrationDate ? p.lastCalibrationDate.split('T')[0] : '',
      nextCalibrationDate: p.nextCalibrationDate ? p.nextCalibrationDate.split('T')[0] : '',
      numeroCertificado: p.numeroCertificado || '',
      laboratorioId: p.laboratorioId || '',
      incertezaExpandida: p.incertezaExpandida != null ? String(p.incertezaExpandida) : '',
      fatorK: p.fatorK != null ? String(p.fatorK) : '2',
    });
    setErro('');
    setShowModal(true);
  }

  async function salvar() {
    if (!form.tag.trim()) return setErro('TAG é obrigatória.');
    if (!form.descricao.trim()) return setErro('Descrição é obrigatória.');
    setSalvando(true);
    setErro('');
    try {
      const body = {
        ...form,
        faixaMin: form.faixaMin !== '' ? parseFloat(form.faixaMin) : null,
        faixaMax: form.faixaMax !== '' ? parseFloat(form.faixaMax) : null,
        lastCalibrationDate: form.lastCalibrationDate || null,
        nextCalibrationDate: form.nextCalibrationDate || null,
        laboratorioId: form.laboratorioId || null,
        incertezaExpandida: form.incertezaExpandida !== '' ? parseFloat(form.incertezaExpandida) : null,
        fatorK: form.fatorK !== '' ? parseFloat(form.fatorK) : 2,
      };
      if (editando) {
        await api.put(`/padroes-referencia/${editando.id}`, body);
      } else {
        await api.post('/padroes-referencia', body);
      }
      setShowModal(false);
      await carregar();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar.');
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(p) {
    if (!window.confirm(`Excluir padrão "${p.tag}"? O registro não será apagado permanentemente.`)) return;
    try {
      await api.delete(`/padroes-referencia/${p.id}`);
      await carregar();
    } catch {
      alert('Erro ao excluir padrão.');
    }
  }

  const filtered = padroes.filter(p => {
    if (search && !p.tag.toLowerCase().includes(search.toLowerCase()) && !p.descricao.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterTipo && p.tipo !== filterTipo) return false;
    if (filterOrigem && p.origem !== filterOrigem) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Padrões de Referência</h1>
          <p className="text-xs text-slate-500 mt-1">Instrumentos padrão utilizados nas calibrações (ISO 10012 cláusula 7.5)</p>
        </div>
        {canEdit && (
          <button
            onClick={abrirNovo}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors"
          >
            + Novo Padrão
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="space-y-3">
        {/* Tabs interno/externo */}
        <div className="flex gap-1 bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg p-1 w-fit">
          {[{v:'', l:'Todos'},{v:'interno', l:'🏭 Internos'},{v:'externo', l:'🔬 Externos'}].map(t => (
            <button key={t.v} onClick={() => setFilterOrigem(t.v)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${filterOrigem === t.v ? 'bg-blue-700 text-white' : 'text-slate-400 hover:text-slate-200'}`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex gap-3 flex-wrap">
          <input type="text" placeholder="Buscar por TAG ou descrição..." value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-600 outline-none focus:border-blue-600 w-64" />
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
            className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] outline-none">
            <option value="">Todos os tipos</option>
            {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="text-xs text-slate-500 self-center">{filtered.length} padrão(ões)</span>
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div className="text-slate-400 text-sm">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <p className="text-3xl mb-3">📐</p>
          <p>Nenhum padrão de referência cadastrado.</p>
          {canEdit && <button onClick={abrirNovo} className="mt-3 text-blue-400 hover:text-blue-300 text-sm">Cadastrar primeiro padrão →</button>}
        </div>
      ) : (
        <div className="bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--surface-4)]">
              <tr className="text-slate-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3">TAG</th>
                <th className="text-left px-4 py-3">Descrição</th>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-left px-4 py-3">Grandeza</th>
                <th className="text-left px-4 py-3">Certificado</th>
                <th className="text-left px-4 py-3">Vencimento</th>
                <th className="text-left px-4 py-3">Incerteza U</th>
                <th className="text-left px-4 py-3">Laboratório</th>
                {canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--surface-4)]">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-[var(--surface-4)] transition-colors">
                  <td className="px-4 py-3 font-mono font-semibold text-slate-200">{p.tag}</td>
                  <td className="px-4 py-3 text-slate-300 max-w-xs truncate">{p.descricao}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${TIPO_COLOR[p.tipo] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                      {TIPO_LABEL[p.tipo] || p.tipo}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{p.grandeza || '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs font-mono">{p.numeroCertificado || '—'}</td>
                  <td className="px-4 py-3"><ExpiryBadge date={p.nextCalibrationDate} /></td>
                  <td className="px-4 py-3 text-slate-400 text-xs">
                    {p.incertezaExpandida != null ? `±${p.incertezaExpandida} (k=${p.fatorK ?? 2})` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {p.laboratorio ? (
                      <button onClick={() => setLabPopup(p.laboratorio)}
                        className="text-xs text-blue-400 hover:text-blue-300 hover:underline transition-colors">
                        {p.laboratorio.nome}
                      </button>
                    ) : (
                      <span className="text-slate-600 text-xs">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => abrirEditar(p)} className="text-xs text-slate-400 hover:text-white transition-colors">✏️ Editar</button>
                        <button onClick={() => excluir(p)} className="text-xs text-red-400 hover:text-red-300 transition-colors">Excluir</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {/* Popup: detalhe do laboratório */}
      {labPopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setLabPopup(null)}>
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-white">🔬 {labPopup.nome}</h3>
              <button onClick={() => setLabPopup(null)} className="text-slate-500 hover:text-white text-xl">×</button>
            </div>
            <div className="space-y-2 text-sm">
              {labPopup.cnpj     && <div className="flex justify-between"><span className="text-slate-500">CNPJ</span><span className="text-slate-300 font-mono">{labPopup.cnpj}</span></div>}
              {labPopup.email    && <div className="flex justify-between"><span className="text-slate-500">E-mail</span><span className="text-slate-300">{labPopup.email}</span></div>}
              {labPopup.telefone && <div className="flex justify-between"><span className="text-slate-500">Telefone</span><span className="text-slate-300">{labPopup.telefone}</span></div>}
              {labPopup.contato  && <div className="flex justify-between"><span className="text-slate-500">Contato</span><span className="text-slate-300">{labPopup.contato}</span></div>}
              {labPopup.acreditacao && <div className="flex justify-between"><span className="text-slate-500">Acreditação</span><span className="text-slate-300">{labPopup.acreditacao}</span></div>}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="px-6 py-4 border-b border-[var(--surface-4)] flex items-center justify-between sticky top-0 bg-[var(--surface-2)]">
              <h2 className="text-base font-semibold text-white">{editando ? 'Editar Padrão' : 'Novo Padrão de Referência'}</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-500 hover:text-white text-xl">×</button>
            </div>

            <div className="p-6 space-y-5">
              {/* Identificação */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Identificação</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs text-slate-400 mb-1 block">Origem</label>
                    <div className="flex gap-2">
                      {[{v:'interno',l:'🏭 Interno (próprio)'},{v:'externo',l:'🔬 Externo (terceiro)'}].map(o => (
                        <button key={o.v} type="button" onClick={() => setForm(f => ({ ...f, origem: o.v }))}
                          className={`flex-1 py-2 px-3 rounded-lg border text-xs font-medium transition-all ${form.origem === o.v ? 'bg-blue-700 border-blue-500 text-white' : 'bg-[var(--surface-1)] border-[var(--surface-4)] text-slate-400 hover:border-slate-500'}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">TAG <span className="text-red-400">*</span></label>
                    <input value={form.tag} onChange={e => setForm(f => ({ ...f, tag: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500"
                      placeholder="Ex: PT-001" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 flex items-center">
                      Tipo
                      <InfoTooltip>
                        <p className="font-semibold text-slate-200 mb-1">Hierarquia metrológica do padrão</p>
                        <div className="space-y-1.5">
                          <div><span className="text-purple-300 font-medium">Primário</span> — máxima exatidão, rastreável diretamente ao SI (INMETRO/BIPM). Raramente usado na indústria.</div>
                          <div><span className="text-blue-300 font-medium">Secundário</span> — calibrado contra um primário. Típico de laboratórios RBC.</div>
                          <div><span className="text-emerald-300 font-medium">Trabalho</span> — o mais comum. Usado no dia a dia para calibrar os instrumentos do processo (ex: termômetro padrão interno).</div>
                          <div><span className="text-amber-300 font-medium">Transferência</span> — intermediário entre dois laboratórios quando a comparação direta não é possível.</div>
                        </div>
                      </InfoTooltip>
                    </label>
                    <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none">
                      {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-xs text-slate-400 mb-1 block">Descrição <span className="text-red-400">*</span></label>
                    <input value={form.descricao} onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500"
                      placeholder="Ex: Termômetro de referência RBC" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Fabricante</label>
                    <input value={form.fabricante} onChange={e => setForm(f => ({ ...f, fabricante: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Modelo</label>
                    <input value={form.modelo} onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Nº de Série</label>
                    <input value={form.numeroDeSerie} onChange={e => setForm(f => ({ ...f, numeroDeSerie: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                </div>
              </div>

              {/* Grandeza e faixa */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Grandeza e Faixa</p>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-2"><label className="text-xs text-slate-400 mb-1 block">Grandeza</label>
                    <input value={form.grandeza} onChange={e => setForm(f => ({ ...f, grandeza: e.target.value }))}
                      placeholder="Ex: Temperatura" className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Unidade</label>
                    <input value={form.unidade} onChange={e => setForm(f => ({ ...f, unidade: e.target.value }))}
                      placeholder="Ex: °C" className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div />
                  <div><label className="text-xs text-slate-400 mb-1 block">Faixa Mín</label>
                    <input type="number" step="any" value={form.faixaMin} onChange={e => setForm(f => ({ ...f, faixaMin: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Faixa Máx</label>
                    <input type="number" step="any" value={form.faixaMax} onChange={e => setForm(f => ({ ...f, faixaMax: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                </div>
              </div>

              {/* Certificado */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Certificado de Calibração</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 mb-1 block">Nº Certificado</label>
                    <input value={form.numeroCertificado} onChange={e => setForm(f => ({ ...f, numeroCertificado: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Laboratório</label>
                    <select value={form.laboratorioId} onChange={e => setForm(f => ({ ...f, laboratorioId: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none">
                      <option value="">— Selecionar —</option>
                      {laboratorios.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                    </select>
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Última Calibração</label>
                    <input type="date" value={form.lastCalibrationDate} onChange={e => setForm(f => ({ ...f, lastCalibrationDate: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Próxima Calibração (Vencimento)</label>
                    <input type="date" value={form.nextCalibrationDate} onChange={e => setForm(f => ({ ...f, nextCalibrationDate: e.target.value }))}
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                </div>
              </div>

              {/* Incerteza */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Incerteza do Certificado (GUM)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 mb-1 block">Incerteza Expandida U</label>
                    <input type="number" step="any" value={form.incertezaExpandida} onChange={e => setForm(f => ({ ...f, incertezaExpandida: e.target.value }))}
                      placeholder="Ex: 0.05"
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Fator k</label>
                    <input type="number" step="0.1" value={form.fatorK} onChange={e => setForm(f => ({ ...f, fatorK: e.target.value }))}
                      placeholder="2"
                      className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
                  </div>
                </div>
                <p className="text-[11px] text-slate-600 mt-2">
                  u_b = U / k = incerteza tipo B do padrão utilizada no cálculo GUM
                </p>
              </div>

              {erro && <p className="text-red-400 text-sm">{erro}</p>}
            </div>

            <div className="px-6 py-4 border-t border-[var(--surface-4)] flex justify-end gap-3 sticky bottom-0 bg-[var(--surface-2)]">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
                Cancelar
              </button>
              <button
                onClick={salvar}
                disabled={salvando}
                className="px-5 py-2 bg-blue-700 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
              >
                {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Criar padrão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
